import { GripVertical, Maximize2 } from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@monorepo-apps/ui/src/components/select";
import { cn } from "@monorepo-apps/ui/src/lib/utils";

import {
  BACKLOG_VALUE,
  UNASSIGNED_VALUE,
} from "@/presentation/components/delivery-tasks/delivery-task-fields";
import {
  PRIORITY_LABEL,
  StatusDot,
  TASK_STATUS_LABEL,
  priorityToTone,
  taskStatusToTone,
} from "@/presentation/components/status/status-dot";
import { format, parseISO } from "date-fns";
import type { DeliveryTaskPatch } from "@/infrastructure/repositories/delivery-tasks.supabase.repository";
import type {
  DeliveryTaskRow,
  Priority,
  ProfileRow,
  SprintRow,
  TaskStatus,
} from "@/infrastructure/supabase/database.types";

const STATUSES: TaskStatus[] = ["todo", "in_progress", "done", "blocked"];
const TRIGGER =
  "hover:border-border/60 h-7 gap-1.5 border-transparent bg-transparent px-2 [&>svg:last-child]:opacity-50";

/**
 * One task line. Draggable onto another sprint's section; the Sprint select is
 * the keyboard-accessible equivalent of that drag.
 */
export function DeliveryTaskRowItem({
  task,
  sprints,
  assignees,
  blockCode,
  assigneeName,
  onPatch,
  onOpen,
  onDragStart,
  onDragEnd,
  isDragging,
}: {
  task: DeliveryTaskRow;
  sprints: SprintRow[];
  assignees: ProfileRow[];
  blockCode: string | null;
  assigneeName: string | null;
  onPatch: (patch: DeliveryTaskPatch) => void;
  onOpen: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  isDragging: boolean;
}) {
  return (
    <tr
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", task.id);
        e.dataTransfer.effectAllowed = "move";
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      className={cn(
        "border-border/40 hover:bg-muted/30 group border-t print:break-inside-avoid",
        isDragging && "opacity-40"
      )}
    >
      <td className="w-6 py-1.5 print:hidden">
        <GripVertical
          className="text-muted-foreground size-3.5 cursor-grab opacity-0 transition group-hover:opacity-60"
          aria-hidden
        />
      </td>
      <td className="py-1.5 pr-4">
        <input
          key={task.title}
          type="text"
          defaultValue={task.title}
          aria-label="Edit task title"
          maxLength={300}
          className="focus:border-ring focus:bg-background hover:border-border/60 w-full truncate rounded-sm border border-transparent bg-transparent px-1.5 py-1 text-xs transition outline-none"
          onBlur={(e) => {
            const next = e.target.value.trim();
            if (next && next !== task.title) onPatch({ title: next });
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.blur();
            } else if (e.key === "Escape") {
              e.currentTarget.value = task.title;
              e.currentTarget.blur();
            }
          }}
        />
      </td>
      <td className="py-1.5 pr-4">
        {blockCode ? (
          <span className="font-data border-border/60 text-muted-foreground rounded border px-1.5 py-0.5 text-[9px] tracking-[0.12em] uppercase">
            {blockCode}
          </span>
        ) : (
          <span className="text-muted-foreground/50 text-[11px]">—</span>
        )}
      </td>
      <td className="py-1.5 pr-4">
        <Select value={task.status} onValueChange={(v) => onPatch({ status: v as TaskStatus })}>
          <SelectTrigger className={TRIGGER} aria-label="Change status">
            <div className="inline-flex items-center gap-2">
              <StatusDot tone={taskStatusToTone(task.status)} />
              <span className="text-[11px]">{TASK_STATUS_LABEL[task.status]}</span>
            </div>
          </SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                <span className="inline-flex items-center gap-2">
                  <StatusDot tone={taskStatusToTone(s)} /> {TASK_STATUS_LABEL[s]}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="py-1.5 pr-4">
        <Select value={task.priority} onValueChange={(v) => onPatch({ priority: v as Priority })}>
          <SelectTrigger className={TRIGGER} aria-label="Change priority">
            <div className="inline-flex items-center gap-2">
              <StatusDot tone={priorityToTone(task.priority)} />
              <span className="text-[10px] tracking-wide uppercase">{task.priority}</span>
            </div>
          </SelectTrigger>
          <SelectContent>
            {(["low", "medium", "high"] as Priority[]).map((p) => (
              <SelectItem key={p} value={p}>
                <span className="inline-flex items-center gap-2">
                  <StatusDot tone={priorityToTone(p)} /> {PRIORITY_LABEL[p]}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="py-1.5 pr-4">
        <Select
          value={task.assignee_id ?? UNASSIGNED_VALUE}
          onValueChange={(v) => onPatch({ assignee_id: v === UNASSIGNED_VALUE ? null : v })}
        >
          <SelectTrigger className={TRIGGER} aria-label="Change assignee">
            <span
              className={cn("max-w-32 truncate text-xs", !assigneeName && "text-muted-foreground")}
            >
              {assigneeName ?? "Unassigned"}
            </span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={UNASSIGNED_VALUE}>
              <span className="text-muted-foreground">Unassigned</span>
            </SelectItem>
            {assignees.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.email ?? "(no email)"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="py-1.5 pr-4 print:hidden">
        <Select
          value={task.sprint_id ?? BACKLOG_VALUE}
          onValueChange={(v) => onPatch({ sprint_id: v === BACKLOG_VALUE ? null : v })}
        >
          <SelectTrigger className={TRIGGER} aria-label="Move to sprint">
            <span className="text-muted-foreground max-w-28 truncate text-[11px]">
              {sprints.find((s) => s.id === task.sprint_id)?.name ?? "Backlog"}
            </span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={BACKLOG_VALUE}>Backlog</SelectItem>
            {sprints.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="text-muted-foreground font-data py-2 pr-4 text-[11px] tabular-nums">
        {/* `parseISO`, not `new Date(...)`: a bare yyyy-MM-dd parses as UTC
            midnight through the Date constructor and renders as the previous
            day west of UTC. `parseISO` reads a date-only string as LOCAL
            midnight, which is what a whole-day value means. */}
        {task.due_date ? format(parseISO(task.due_date), "d MMM") : "—"}
      </td>
      <td className="py-2 print:hidden">
        <button
          type="button"
          onClick={onOpen}
          className="text-muted-foreground hover:bg-muted hover:text-foreground flex size-7 items-center justify-center rounded-sm opacity-0 transition group-hover:opacity-100 focus-visible:opacity-100"
          aria-label={`Open details for "${task.title}"`}
          title="Open detail"
        >
          <Maximize2 className="size-3.5" />
        </button>
      </td>
    </tr>
  );
}
